import type { UseModalState } from "@clipboard-health/ui-components";
import { z } from "zod";

import { type QuestionTag } from "../redesign/Workplace/Review/types";

export type PostShiftReviewStep =
  | "pick-rating"
  | "write-review"
  | QuestionTag.SOFTWARE
  | QuestionTag.SUPPLIES
  | QuestionTag.PATIENTS_PER_NURSE
  | QuestionTag.PARKING
  | "end";

export interface WriteReviewStepData {
  reviewText: string;
}

export interface SoftwareQuestionStepData {
  answer: string;
  otherText?: string;
}

export interface SuppliesQuestionStepData {
  answer: string;
}

export interface PatientCountQuestionStepData {
  answer: string;
}

export interface ParkingQuestionStepData {
  parkingAnswer: string | undefined;
}

// Union type for all possible step data
export type StepActionData =
  | WriteReviewStepData
  | SoftwareQuestionStepData
  | SuppliesQuestionStepData
  | PatientCountQuestionStepData
  | ParkingQuestionStepData;

export interface StepAction {
  type: "SUBMIT_RATING" | "NEXT_STEP" | "BACK_STEP" | "SKIP_STEP" | "CLOSE_FLOW" | "SUBMIT_REVIEW";
  rating?: number;
  data?: StepActionData;
}
export { QuestionTag } from "../redesign/Workplace/Review/types";

export interface QuestionData {
  tags?: Array<{ tag: QuestionTag }>;
}

export interface QuestionsResponse {
  data: QuestionData[];
}

export interface PostShiftReviewFlowProps {
  modalState: UseModalState;
  facility: {
    id: string;
    name: string;
    type: string;
    photo?: string;
  };
  shiftId?: string;
  onComplete: () => void;
}

export interface QuestionMappings {
  commentQuestionId: string | undefined;
  tagToQuestionId: Partial<Record<QuestionTag, string>>;
  questionOptions: Record<string, Array<{ id: string; displayText: string }>>;
}

export const postShiftReviewFormSchema = z.object({
  rating: z.number().min(1, "Please rate your shift"),
  isAnonymous: z.boolean().optional(), // Allow unselected state initially
  reviewText: z.string(),
  softwareAnswer: z.string().optional(),
  softwareOtherText: z.string().optional(),
  suppliesAnswer: z.string().optional(),
  patientCountAnswer: z.string().optional(),
  parkingAnswer: z.string().optional(),
});

export type PostShiftReviewFormValues = z.infer<typeof postShiftReviewFormSchema>;

export interface PostShiftReviewFlowContentProps extends PostShiftReviewFlowProps {
  questionMappings?: QuestionMappings;
  isLoading: boolean;
  isError: boolean;
  onSubmitRating?: (rating: number) => Promise<void>;
  onSubmitReview: (formData: PostShiftReviewFormValues) => Promise<void>;
}
